/** Booking times as the member portal words them (#401). Fixed English names — never ``toLocaleString``,
 *  which follows the browser locale. Shared by Booking costs (MR B) and booking approval (MR D).
 *
 *  Both formatters take an optional IANA ``timeZone`` — the global constraints require booking times to
 *  show in the room's SITE zone, not the viewer's browser. Pass the ``site_time_zone`` the API already
 *  sends on the booking/request/cost row. Omitted, they fall back to the browser's local time. */
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n: number) => String(n).padStart(2, "0");

/** The wall-clock fields of ``iso`` as read in ``timeZone`` (fixed "en-US" output, never the browser locale). */
function zonedParts(iso: string, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return { weekday: get("weekday"), day: get("day"), month: get("month"), hour: get("hour"), minute: get("minute") };
}

/** "14:00", in ``timeZone`` when given, else the browser's local time. */
export function clock(iso: string, timeZone?: string): string {
  if (timeZone) {
    const { hour, minute } = zonedParts(iso, timeZone);
    return `${pad(Number(hour))}:${pad(Number(minute))}`;
  }
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "Fri 9 Oct 14:00", in ``timeZone`` when given, else the browser's local time. */
export function dayClock(iso: string, timeZone?: string): string {
  if (timeZone) {
    const { weekday, day, month } = zonedParts(iso, timeZone);
    return `${weekday} ${day} ${month} ${clock(iso, timeZone)}`;
  }
  const d = new Date(iso);
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]} ${clock(iso)}`;
}
